/**
 * Theme glue for engraving and apparel live preview builders.
 * Visualization loads remotely (iface 2 / 3). Failures are silent.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  buildInitialLivePreviewState,
  getLivePreviewAlignment,
  normalizeLivePreviewConfig,
  parseConfigJson,
} from '@live-preview/config';
import {
  DEFAULT_ARTWORK_POSITION,
  clampPosition,
  loadArtworkFromFile,
  loadArtworkSource,
  releaseArtworkSource,
} from '@patch/artwork';
import {
  applyAlignment,
  describeMedia,
  listMedia,
  movePreviewAdjustment,
  resizePreviewAdjustment,
} from '@patch/alignment';
import { attachFileToInput, resolveTargetAspectRatio } from '@patch/export';
import { createCleanedPreview } from '@patch/background-cleanup';
import { createRemotePreviewRenderer } from '@preview/remote';
import { packageArtworkForOrder } from '@live-preview/artwork-order-package';
import { resolvePreviewPlacement, previewSurfacePoint } from '@live-preview/positioning';
import { clearPerspectiveSurface } from '@patch/perspective-surface';

const SNAPSHOT_DEBOUNCE = 700;
const ARTWORK_HIGHLIGHT_DURATION = 2400;
/** @returns {{
 * state: ReturnType<typeof buildInitialLivePreviewState> | null,
 * artwork: import('./patch-artwork.js').ArtworkSource | null,
 * displayArtwork: import('./patch-artwork.js').ArtworkSource | null,
 * originalFile: File | null, packagedFile: File | null, token: number, pending: Promise<void> | null
 * }} */
function createArtworkSide() {
  return { state: null, artwork: null, displayArtwork: null, originalFile: null, packagedFile: null, token: 0, pending: null };
}

const ALIGNMENT_EDITOR_PARAMS = ['preview-align', 'patch-align'];

/**
 * @param {() => void} callback
 * @param {number} timeout
 * @returns {number}
 */
function whenIdle(callback, timeout) {
  const idle = /** @type {typeof window.requestIdleCallback | undefined} */ (
    /** @type {any} */ (window).requestIdleCallback
  );
  if (idle) return idle(callback, { timeout });
  return window.setTimeout(callback, timeout);
}

export class LivePreviewOverlay extends HTMLElement {}

if (!customElements.get('live-preview-overlay')) {
  customElements.define('live-preview-overlay', LivePreviewOverlay);
}

/**
 * @typedef {object} LivePreviewRefs
 * @property {HTMLScriptElement} [configData]
 * @property {HTMLScriptElement} [advancedData]
 * @property {HTMLElement} [errors]
 * @property {HTMLElement} [errorHeading]
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLElement} [sampleArtworkLabel]
 * @property {HTMLElement} [qualityWarning]
 * @property {HTMLInputElement} [artworkInput]
 * @property {HTMLInputElement} [artworkSubmissionInput]
 * @property {HTMLElement} [artworkFileName]
 * @property {HTMLButtonElement} [artworkRemove]
 * @property {HTMLButtonElement} [backArtworkRemove]
 * @property {HTMLButtonElement} [artworkChoose]
 * @property {HTMLInputElement} [propMode]
 * @property {HTMLInputElement} [propColor]
 * @property {HTMLInputElement} [propPayload]
 * @property {HTMLInputElement} [propArtworkName]
 * @property {HTMLInputElement} [mockupInput]
 * @property {HTMLInputElement} [vectorInput]
 * @property {HTMLElement} [secondArtworkLabel]
 * @property {HTMLInputElement} [backEnabled]
 * @property {HTMLInputElement} [backArtworkInput]
 * @property {HTMLInputElement} [backArtworkSubmissionInput]
 * @property {HTMLInputElement} [backPropArtworkName]
 * @property {HTMLInputElement} [backMockupInput]
 * @property {HTMLElement} [backArtworkGroup]
 * @property {HTMLElement} [backArtworkFileName]
 * @property {HTMLElement} [backQualityWarning]
 * @property {HTMLElement} [backUnavailable]
 * @property {HTMLElement} [sideControls]
 * @property {HTMLButtonElement} [viewButton]
 * @property {HTMLButtonElement} [backViewButton]
 * @property {HTMLElement[]} [groups]
 *
 * @extends Component<LivePreviewRefs>
 */
export class LivePreviewDesigner extends Component {
  /** @type {import('./live-preview-config.js').LivePreviewConfig | null} */
  #config = null;

  #sides = { front: createArtworkSide(), back: createArtworkSide() };
  #activeSide = 'front';
  /** @type {import('./patch-artwork.js').ArtworkSource | null} */
  #sampleArtwork = null;
  /** @type {import('./patch-artwork.js').ArtworkSource | null} */
  #sampleDisplayArtwork = null;
  #lastFrontMediaId = null;
  #beforeUploadMediaId = null;
  /** @type {any} */
  #snapshotRenderer = null;
  #snapshotWork = null;

  get #state() { return this.#sides[this.#activeSide].state; }
  set #state(value) { this.#sides[this.#activeSide].state = value; }
  get #artwork() { return this.#sides[this.#activeSide].artwork; }
  get #displayArtwork() { return this.#sides[this.#activeSide].displayArtwork || (this.#showingSample ? this.#sampleDisplayArtwork : null); }
  get #showingSample() { return this.#activeSide === 'front' && !this.#artwork && Boolean(this.#sampleDisplayArtwork); }
  get #hasSecondSide() { return this.#iface === 3 && this.dataset.secondSideEnabled === 'true'; }
  get #firstOnBack() { return this.#hasSecondSide && this.querySelector('[data-live-preview-control="first-side"]:checked')?.value === 'Back'; }
  // Keep upload records stable while mapping them to physical garment sides.
  #physicalSide(side) { return this.#firstOnBack ? (side === 'front' ? 'back' : 'front') : side; }
  #sideForMedia(target) {
    const physical = this.#isBackMedia(target) ? 'back' : 'front';
    const side = this.#physicalSide(physical);
    return side === 'back' && !this.#backEnabled ? 'front' : side;
  }
  get #backEnabled() { return this.#hasSecondSide && this.refs.backEnabled?.checked === true; }
  get #uploadImageTag() { return this.#iface === 2 ? this.dataset.uploadImageTag?.trim().toLowerCase() || '' : ''; }
  #sideRef(side, name) {
    return this.refs[side === 'back' ? `back${name[0].toUpperCase()}${name.slice(1)}` : name];
  }

  /** @type {any} */
  #remoteRenderer = null;

  /** @type {LivePreviewOverlay | null} */
  #overlay = null;

  #previewReady = false;
  #remoteSetupAttempts = 0;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #snapshotTimer = null;

  #snapshotToken = 0;

  /** @type {Map<number, PointerEvent>} */
  #pointers = new Map();

  /** @type {{ x: number, y: number, distance: number, scale: number, patchSize: number } | null} */
  #dragOrigin = null;

  #overlayRotation = 0;
  #placementResize = null;
  #placementFrame = 0;
  #placementHost = null;

  /** @type {{ destroy: () => void, syncToMedia: () => void } | null} */
  #alignmentEditor = null;

  /** @returns {number} */
  get #iface() {
    return Number(this.dataset.previewIface || '2') || 2;
  }

  /** @returns {boolean} */
  get #locksPreviewPlacement() {
    return this.dataset.lockPreviewPlacement === 'true';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#activeSide = 'front';
    this.#controller = new AbortController();
    const config = this.#readConfig();
    if (!config) return;

    this.#config = config;
    this.#state = buildInitialLivePreviewState(config);
    this.#sides.back.state = buildInitialLivePreviewState(config);
    this.#bindEvents();
    this.#syncHiddenProperties();
    this.#syncPositionInputs(this.#state.artwork.position);
    this.#syncPreviewAdjustmentInputs(this.#state.preview.adjustment);
    this.#updateGroupVisibility();
    this.#syncSideControls();
    void this.#setupRemotePreview();
    void this.#loadSampleArtwork().catch(() => {});
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#placementResize?.disconnect();
    this.#placementResize = null;
    this.#placementHost = null;
    cancelAnimationFrame(this.#placementFrame);
    if (this.#overlay) clearPerspectiveSurface(this.#overlay);
    if (this.#snapshotTimer) window.clearTimeout(this.#snapshotTimer);
    this.#remoteRenderer?.destroy?.();
    this.#remoteRenderer = null;
    this.#alignmentEditor?.destroy();
    this.#alignmentEditor = null;
    this.#overlay = null;
    this.#snapshotToken += 1;
    this.#snapshotRenderer?.destroy?.();
    this.#snapshotRenderer = null;
    for (const side of ['front', 'back']) {
      this.#sides[side].token += 1;
      this.#setArtwork(null, side);
    }
    if (this.#sampleDisplayArtwork !== this.#sampleArtwork) releaseArtworkSource(this.#sampleDisplayArtwork);
    releaseArtworkSource(this.#sampleArtwork);
    this.#sampleArtwork = null;
    this.#sampleDisplayArtwork = null;
    super.disconnectedCallback?.();
  }

  /**
   * @returns {Promise<boolean>}
   */
  async prepareForSubmit() {
    if (!this.#config || !this.#state) {
      this.#showError(
        this.dataset.errorConfig ||
          'This product is not set up for live preview yet. Please contact us and we will help with your order.'
      );
      return false;
    }

    await Promise.all(Object.values(this.#sides).map((side) => side.pending));
    if (this.dataset.requireArtwork === 'true' && !this.#sides.front.artwork) {
      this.#showError(this.dataset.errorRequireArtwork || 'Please personalize your item to add to cart.');
      this.#highlightArtworkGroup('artwork');
      return false;
    }
    if (this.#backEnabled && !this.#sides.back.artwork) {
      this.#showError(this.dataset.errorBackRequired || 'Please upload your back design.');
      this.#highlightArtworkGroup('back-artwork');
      return false;
    }
    for (const side of ['front', 'back']) {
      if (!(await this.#bindOriginalArtworkToForm(side))) {
        this.#showError(this.dataset.errorFileAttach || "We couldn't attach that artwork to your order. Please choose the file again.");
        return false;
      }
    }
    if (this.#snapshotTimer) window.clearTimeout(this.#snapshotTimer);
    this.#snapshotToken += 1;
    try {
      await this.#queueSnapshot();
    } catch {
      this.#clearGeneratedFiles();
    }
    this.#syncHiddenProperties();

    return true;
  }

  /**
   * @returns {import('./live-preview-config.js').LivePreviewConfig | null}
   */
  #readConfig() {
    const blockConfig = parseConfigJson(this.refs.configData?.textContent ?? null);
    const advancedConfig = parseConfigJson(this.refs.advancedData?.textContent ?? null);
    const metafieldConfig = parseConfigJson(this.dataset.metafieldConfig ?? null);
    const merged = {
      ...(blockConfig && typeof blockConfig === 'object' ? blockConfig : {}),
      ...(advancedConfig && typeof advancedConfig === 'object' ? advancedConfig : {}),
      ...(metafieldConfig && typeof metafieldConfig === 'object' ? metafieldConfig : {}),
      inkColor: this.dataset.inkColor || undefined,
      colorMode: this.dataset.colorMode || undefined,
    };

    return normalizeLivePreviewConfig(merged, {
      colorMode: /** @type {'engraved' | 'full-color' | 'single-color'} */ (
        this.dataset.colorMode || 'engraved'
      ),
      inkColor: this.dataset.inkColor || '#141414',
    });
  }

  #bindEvents() {
    const signal = this.#controller?.signal;
    if (!signal) return;

    this.addEventListener('input', (event) => this.#handleControlEvent(event), { signal });
    this.addEventListener('change', (event) => this.#handleControlEvent(event), { signal });
    this.addEventListener('click', (event) => this.#handleClick(event), { signal });

    this.#bindOverlayPointers(signal);
    this.#bindGalleryRemount(signal);
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindOverlayPointers(signal) {
    const onPointerDown = (event) => {
      if (!(event instanceof PointerEvent) || !this.#overlay || !this.#artwork) return;
      if (this.#alignmentEditor) return;
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      if (!this.#overlay.contains(/** @type {Node} */ (event.target))) return;

      event.preventDefault();
      this.#pointers.set(event.pointerId, event);
      this.#overlay.setPointerCapture?.(event.pointerId);
      this.#dragOrigin = this.#measureDragOrigin();
    };

    const onPointerMove = (event) => {
      if (!(event instanceof PointerEvent) || !this.#dragOrigin || !this.#state) return;
      if (!this.#pointers.has(event.pointerId)) return;
      this.#pointers.set(event.pointerId, event);

      if (this.#pointers.size >= 2) {
        const [a, b] = [...this.#pointers.values()];
        if (!a || !b) return;
        const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
        const ratio = distance / (this.#dragOrigin.distance || distance);
        if (this.#locksPreviewPlacement) {
          const current = this.#state.artwork.position;
          this.#state.artwork.position = clampPosition(
            { ...current, scale: current.scale * ratio },
            current
          );
          this.#syncPositionInputs(this.#state.artwork.position);
        } else {
          this.#state.preview.adjustment = resizePreviewAdjustment(
            this.#state.preview.adjustment,
            ratio
          );
          this.#syncPreviewAdjustmentInputs(this.#state.preview.adjustment);
        }
        this.#dragOrigin.distance = distance;
        this.#render();
        this.#scheduleSnapshot();
        return;
      }

      const current = this.#pointers.get(event.pointerId);
      if (!current || !this.#overlay) return;
      const reference = this.#locksPreviewPlacement ? this.#overlay : this.#overlay.parentElement;
      if (!reference) return;
      const rect = reference.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const movedX = current.clientX - this.#dragOrigin.x;
      const movedY = current.clientY - this.#dragOrigin.y;
      this.#dragOrigin.x = current.clientX;
      this.#dragOrigin.y = current.clientY;

      if (this.#locksPreviewPlacement) {
        if (this.#config?.positioningEnabled) {
          const host = this.#overlay.parentElement;
          const box = host?.getBoundingClientRect();
          const viewBox = this.#overlay.querySelector('svg')?.viewBox.baseVal;
          const alignment = host ? this.#placementAlignment(host) : null;
          if (!box?.width || !box.height || !alignment || alignment.hide) return;
          const local = (x, y) => previewSurfacePoint(alignment, box.width / box.height,
            viewBox?.width / viewBox?.height || 1, (x - box.left) / box.width * 100, (y - box.top) / box.height * 100);
          const before = local(current.clientX - movedX, current.clientY - movedY);
          const after = local(current.clientX, current.clientY);
          if (!before || !after) return;
          const position = this.#state.artwork.position;
          this.#state.artwork.position = clampPosition({ ...position, x: position.x + after.x - before.x, y: position.y + after.y - before.y }, position);
          this.#syncPositionInputs(this.#state.artwork.position);
          this.#render();
          this.#scheduleSnapshot();
          return;
        }
        const radians = (this.#overlayRotation * Math.PI) / 180;
        const localX = movedX * Math.cos(radians) + movedY * Math.sin(radians);
        const localY = -movedX * Math.sin(radians) + movedY * Math.cos(radians);
        const width = this.#overlay.offsetWidth || rect.width;
        const height = this.#overlay.offsetHeight || rect.height;
        const position = this.#state.artwork.position;
        this.#state.artwork.position = clampPosition(
          {
            ...position,
            x: position.x + (localX / width) * 100,
            y: position.y + (localY / height) * 100,
          },
          position
        );
        this.#syncPositionInputs(this.#state.artwork.position);
      } else {
        const deltaX = (movedX / rect.width) * 100;
        const deltaY = (movedY / rect.height) * 100;
        this.#state.preview.adjustment = movePreviewAdjustment(
          this.#state.preview.adjustment,
          deltaX,
          deltaY
        );
        this.#syncPreviewAdjustmentInputs(this.#state.preview.adjustment);
      }
      this.#render();
      this.#scheduleSnapshot();
    };

    const onPointerUp = (event) => {
      if (!(event instanceof PointerEvent)) return;
      this.#pointers.delete(event.pointerId);
      if (this.#pointers.size === 0) this.#dragOrigin = null;
      else this.#dragOrigin = this.#measureDragOrigin();
    };

    document.addEventListener('pointerdown', onPointerDown, { signal, capture: true });
    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  }

  /**
   * @returns {{ x: number, y: number, distance: number, scale: number, patchSize: number }}
   */
  #measureDragOrigin() {
    const points = [...this.#pointers.values()];
    const first = points[0];
    const second = points[1];
    const x = first?.clientX ?? 0;
    const y = first?.clientY ?? 0;
    const distance =
      first && second ? Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) : 1;
    return {
      x,
      y,
      distance,
      scale: this.#state?.artwork.position.scale ?? 1,
      patchSize: this.#state?.preview.adjustment.size ?? 0,
    };
  }

  #findMountRoot() {
    const root = this.#findGalleryRoot();
    const grid = root?.querySelector('.media-gallery__grid');
    return (this.#hasSecondSide || this.#uploadImageTag) && grid && getComputedStyle(grid).display !== 'none' ? grid : root;
  }

  #uploadImageTarget() {
    const tag = this.#uploadImageTag;
    if (!tag || !this.#artwork) return null;
    return this.#eligibleMedia().find((target) => target.querySelector('img')?.getAttribute('alt')?.toLowerCase().includes(tag)) || null;
  }

  #syncUploadImage() {
    if (!this.#uploadImageTag) return;
    const target = this.#uploadImageTarget();
    if (target) {
      const root = this.#findGalleryRoot();
      const current = root?.querySelector('[aria-hidden="false"] .product-media') || this.#remoteRenderer?.mountTarget;
      if (current && current.getAttribute('data-media-id') !== target.getAttribute('data-media-id')) {
        this.#beforeUploadMediaId = current.getAttribute('data-media-id');
      }
      this.#navigateToMedia(target);
    } else if (!this.#artwork && this.#beforeUploadMediaId) {
      const previous = this.#eligibleMedia().find((media) => media.getAttribute('data-media-id') === this.#beforeUploadMediaId);
      if (previous) this.#navigateToMedia(previous);
      this.#beforeUploadMediaId = null;
    }
    this.#remoteRenderer?.setMountRoot?.(this.#findMountRoot());
    this.#remoteRenderer?.refreshMount?.();
  }

  #eligibleMedia(includeInactiveSlides = false) {
    const mediaRoot = this.#findMountRoot();
    const candidates = mediaRoot?.querySelectorAll('.product-media[data-media-id]') || [];
    const media = [];
    for (const target of candidates) {
      const hiddenAncestor = target.closest('[hidden]');
      if (target.closest('dialog') || (hiddenAncestor && !(includeInactiveSlides && hiddenAncestor.matches('slideshow-slide'))) || target.querySelector('deferred-media, product-model')) continue;
      if (!target.querySelector('img') || media.some((entry) => entry.getAttribute('data-media-id') === target.getAttribute('data-media-id'))) continue;
      media.push(target);
    }
    return media;
  }

  #isBackMedia(target) {
    const tag = this.dataset.backImageTag?.trim().toLowerCase();
    return Boolean(this.#hasSecondSide && tag && target?.querySelector('img')?.getAttribute('alt')?.toLowerCase().includes(tag));
  }

  #backImageTarget(includeInactiveSlides = false) {
    const candidates = this.#eligibleMedia(includeInactiveSlides).filter((target) => this.#isBackMedia(target));
    if (this.dataset.backColorMatching !== 'true') return candidates[0] || null;
    const tag = this.#imageTagHandle(this.dataset.backImageTag || '');
    const color = this.dataset.backColorValue || '';
    const tokens = (target) => (target.querySelector('img')?.getAttribute('alt') || '').split(/\s+/).map((token) => this.#imageTagHandle(token));
    return (color && candidates.find((target) => tokens(target).includes(`${color}-${tag}`)))
      || candidates.find((target) => !tokens(target).some((token) => token.endsWith(`-${tag}`))) || null;
  }

  #imageTagHandle(value) {
    return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  #targetForSide(side) {
    const media = this.#eligibleMedia();
    if (this.#physicalSide(side) === 'back') return this.#backImageTarget();
    return media.find((target) => !this.#isBackMedia(target) && target.getAttribute('data-media-id') === this.#lastFrontMediaId)
      || media.find((target) => !this.#isBackMedia(target)) || null;
  }

  #syncSideControls() {
    if (!this.#hasSecondSide) return;
    const backTarget = this.#targetForSide('back');
    this.refs.backEnabled?.setAttribute('aria-expanded', String(this.#backEnabled));
    if (this.refs.backArtworkGroup) this.refs.backArtworkGroup.hidden = !this.#backEnabled;
    if (this.refs.sideControls) this.refs.sideControls.hidden = !this.#backEnabled || !backTarget;
    if (this.refs.backUnavailable) this.refs.backUnavailable.hidden = !(this.#backEnabled || this.#firstOnBack) || Boolean(this.#backImageTarget());
    if (this.refs.secondArtworkLabel) this.refs.secondArtworkLabel.textContent = this.#firstOnBack
      ? this.refs.secondArtworkLabel.dataset.frontLabel : this.refs.secondArtworkLabel.dataset.backLabel;
    for (const side of ['front', 'back']) {
      this.#sideRef(side, 'viewButton')?.setAttribute('aria-pressed', String(this.#physicalSide(this.#activeSide) === side));
    }
    if (!this.#backEnabled && this.refs.backMockupInput) attachFileToInput(this.refs.backMockupInput, null);
    this.#syncHiddenProperties();
  }

  #selectSide(side, target = undefined, navigate = true) {
    if (!this.#hasSecondSide) return;
    if (side === 'back' && !this.#backEnabled) side = 'front';
    this.#activeSide = side;
    this.#pointers.clear();
    this.#dragOrigin = null;
    const media = target === undefined ? this.#targetForSide(side) : target;
    this.#state.preview.mediaId = media?.getAttribute('data-media-id') || null;
    if (media && !this.#isBackMedia(media)) this.#lastFrontMediaId = this.#state.preview.mediaId;
    this.#remoteRenderer?.setArtwork?.(this.#displayArtwork);
    this.#syncPositionInputs(this.#state.artwork.position);
    this.#syncPreviewAdjustmentInputs(this.#state.preview.adjustment);
    this.#syncSideControls();
    if (navigate && media) this.#navigateToMedia(media);
    this.#remoteRenderer?.setMountRoot?.(this.#findMountRoot());
    this.#remoteRenderer?.refreshMount?.();
    this.#render();
    this.#scheduleSnapshot();
  }

  #navigateToMedia(media) {
    const mediaId = media.getAttribute('data-media-id');
    const root = this.#findGalleryRoot();
    const slideshow = root?.querySelector('slideshow-component');
    const slide = [...(slideshow?.querySelectorAll('slideshow-slide') || [])].find((item) => item.querySelector('.product-media')?.getAttribute('data-media-id') === mediaId);
    if (slide && slideshow?.select && getComputedStyle(slideshow).display !== 'none') {
      slideshow.select({ id: slide.getAttribute('slide-id') }, undefined, { animate: false });
    } else {
      const gridMedia = [...(root?.querySelectorAll('.media-gallery__grid .product-media') || [])].find((item) => item.getAttribute('data-media-id') === mediaId);
      gridMedia?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  #canPreviewTarget(target) {
    if (this.#alignmentEditor && target && this.#eligibleMedia().includes(target)) return true;
    if (!this.#hasSecondSide) return true;
    return Boolean(target && this.#eligibleMedia().some((entry) => entry.getAttribute('data-media-id') === target.getAttribute('data-media-id'))
      && ((this.#activeSide !== 'back' || this.#backEnabled) && (this.#physicalSide(this.#activeSide) === 'back' ? this.#isBackMedia(target) && (this.dataset.backColorMatching !== 'true' || target.getAttribute('data-media-id') === this.#backImageTarget()?.getAttribute('data-media-id')) : !this.#isBackMedia(target))));
  }

  #bindGalleryRemount(signal) {
    const section = this.closest('.shopify-section');
    let remountTimer = /** @type {number | null} */ (null);
    let navigateAfterRemount = false;
    const scheduleRemount = () => {
      if (remountTimer != null) window.clearTimeout(remountTimer);
      remountTimer = window.setTimeout(() => {
        remountTimer = null;
        if (!this.isConnected) return;
        const navigate = navigateAfterRemount;
        navigateAfterRemount = false;
        this.#syncSideControls();
        this.#remoteRenderer?.setMountRoot?.(this.#findMountRoot());
        if (this.#hasSecondSide) this.#selectSide(this.#activeSide, undefined, navigate);
        else { this.#syncUploadImage(); this.#remoteRenderer?.refreshMount?.(); this.#render(); }
      }, 50);
    };

    const applyVariantSelection = (resource, html) => {
      if (signal.aborted || !this.isConnected) return;
      if (this.dataset.backColorMatching === 'true') {
        const updated = html?.querySelector(`apparel-preview[data-form-id="${CSS.escape(this.dataset.formId || '')}"]`);
        const index = Number.parseInt(this.dataset.backColorOptionIndex || '', 10);
        const option = resource?.options?.[index];
        if (typeof option === 'string') this.dataset.backColorValue = this.#imageTagHandle(option);
        else if (updated) this.dataset.backColorValue = updated.dataset.backColorValue || '';
      }
      navigateAfterRemount = true;
      scheduleRemount();
    };
    section?.addEventListener(ThemeEvents.variantUpdate, (event) => {
      applyVariantSelection(event.detail?.resource, event.detail?.data?.html);
    }, { signal });
    // Vero2's gallery updates after the Shopify product-selection promise resolves.
    // Keep this optional so themes with the legacy event map still work.
    let selectionGeneration = 0;
    void import('@shopify/events').then((mod) => {
      const eventName = mod.StandardEvents?.productSelect;
      if (!eventName || signal.aborted) return;
      section?.addEventListener(eventName, (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
        const generation = ++selectionGeneration;
        if (!event.promise?.then) return;
        event.promise.then(({ detail }) => {
          if (generation !== selectionGeneration) return;
          applyVariantSelection(detail?.resource, detail?.html);
        }).catch(() => {});
      }, { signal });
    }).catch(() => {});
    if (this.#hasSecondSide || this.#uploadImageTag) window.addEventListener('resize', scheduleRemount, { signal });
    section?.addEventListener('slideshow:select', (event) => {
      if (!this.#hasSecondSide || !this.#findGalleryRoot()?.contains(event.target) || event.target.closest('dialog')) return;
      const target = event.detail?.slide?.querySelector('.product-media');
      if (!target) return;
      const side = this.#sideForMedia(target);
      this.#selectSide(side, target, false);
    }, { signal });

    section?.addEventListener('click', (event) => {
      if (!this.#hasSecondSide || !(event.target instanceof Element)) return;
      const container = event.target.closest('.media-gallery__grid .product-media-container');
      const target = container?.querySelector('.product-media');
      if (target) this.#selectSide(this.#sideForMedia(target), target, false);
    }, { signal });

    if (section) {
      let galleryRoot = this.#findGalleryRoot();
      const observer = new MutationObserver(() => {
        const root = this.#findGalleryRoot();
        if (root !== galleryRoot || (this.#overlay?.isConnected && root && !root.contains(this.#overlay))) {
          galleryRoot = root;
          scheduleRemount();
        }
      });
      observer.observe(section, { childList: true, subtree: true });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
  }

  /**
   * @param {Event} event
   */
  #handleControlEvent(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.#state) return;

    const control = target.dataset.livePreviewControl || target.dataset.patchControl;
    if (!control) return;

    if (target instanceof HTMLInputElement && target.type === 'file') {
      if (event.type !== 'change') return;
      const side = control === 'back-artwork' ? 'back' : 'front';
      this.#sides[side].pending = this.#handleFileSelected(target, side);
      return;
    }

    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    const { value } = target;

    switch (control) {
      case 'first-side':
        if (event.type !== 'change') return;
        for (const radio of this.querySelectorAll('[data-live-preview-control="first-side"]')) {
          radio.dataset.previousChecked = String(radio.dataset.currentChecked === 'true');
          radio.dataset.currentChecked = String(radio === target);
        }
        this.#clearError();
        this.#selectSide('front');
        break;
      case 'back-enabled':
        this.#snapshotToken += 1;
        this.#clearError();
        this.#syncSideControls();
        void this.#bindOriginalArtworkToForm('back');
        this.#selectSide(this.#backEnabled ? 'back' : 'front');
        break;
      case 'position-x':
      case 'position-y':
      case 'position-scale':
      case 'position-rotation':
        this.#updatePosition(control, Number.parseFloat(value));
        break;
      case 'preview-x':
      case 'preview-y':
      case 'preview-size':
        this.#updatePreviewAdjustment(control, Number.parseFloat(value));
        break;
      default:
        break;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const action =
      target.closest('[data-live-preview-action]')?.getAttribute('data-live-preview-action') ||
      target.closest('[data-patch-action]')?.getAttribute('data-patch-action');
    if (!action) return;

    switch (action) {
      case 'upload':
        event.preventDefault();
        this.refs.artworkInput?.click();
        break;
      case 'upload-back':
        event.preventDefault();
        this.refs.backArtworkInput?.click();
        break;
      case 'view-front':
      case 'view-back':
        event.preventDefault();
        this.#selectSide(this.#physicalSide(action === 'view-back' ? 'back' : 'front'));
        break;
      case 'remove-back-artwork':
        event.preventDefault();
        this.#clearArtwork('back');
        break;
      case 'reset-artwork':
        event.preventDefault();
        if (!this.#state) return;
        this.#state.artwork.position = { ...DEFAULT_ARTWORK_POSITION };
        this.#syncPositionInputs(this.#state.artwork.position);
        this.#render();
        this.#scheduleSnapshot();
        break;
      case 'reset-preview':
        event.preventDefault();
        if (!this.#state) return;
        this.#state.preview.adjustment = { x: 0, y: 0, size: 0 };
        this.#syncPreviewAdjustmentInputs(this.#state.preview.adjustment);
        this.#render();
        this.#scheduleSnapshot();
        break;
      case 'remove-artwork':
        event.preventDefault();
        this.#clearArtwork('front');
        break;
      default:
        break;
    }
  }

  /**
   * @param {string} control
   * @param {number} value
   */
  #updatePosition(control, value) {
    if (!this.#state || !Number.isFinite(value)) return;
    const next = { ...this.#state.artwork.position };
    if (control === 'position-x') next.x = value;
    if (control === 'position-y') next.y = value;
    if (control === 'position-scale') next.scale = value;
    if (control === 'position-rotation') next.rotation = value;
    this.#state.artwork.position = clampPosition(next, this.#state.artwork.position);
    this.#render();
    this.#scheduleSnapshot();
  }

  /**
   * @param {string} control
   * @param {number} value
   */
  #updatePreviewAdjustment(control, value) {
    if (!this.#state || !Number.isFinite(value)) return;
    const next = { ...this.#state.preview.adjustment };
    if (control === 'preview-x') next.x = value;
    if (control === 'preview-y') next.y = value;
    if (control === 'preview-size') next.size = value;
    this.#state.preview.adjustment = next;
    this.#render();
    this.#scheduleSnapshot();
  }

  /**
   * @param {HTMLInputElement} input
   */
  async #handleFileSelected(input, side = 'front') {
    const file = input.files?.[0];
    const config = this.#config;
    const record = this.#sides[side];
    if (!file || !config || !record.state) return;
    const token = ++record.token;
    const result = await loadArtworkFromFile(file, config.artwork, this.#artworkMessages());
    if (!this.isConnected || token !== record.token) {
      if ('source' in result) releaseArtworkSource(result.source);
      return;
    }
    if ('error' in result) {
      this.#showError(result.error);
      input.value = '';
      return;
    }
    this.#clearError();
    this.#clearGeneratedFiles();
    this.#snapshotToken += 1;
    record.originalFile = file;
    record.packagedFile = null;
    record.state.artwork.fileName = file.name;
    this.#setArtwork(result.source, side);
    await this.#bindOriginalArtworkToForm(side);
    if (token !== record.token || !this.isConnected) return;
    const fileName = this.#sideRef(side, 'artworkFileName');
    if (fileName) fileName.textContent = file.name;
    this.#updateQualityWarning(result.source, side);
    if (this.#hasSecondSide && (side === 'front' || this.#backEnabled)) this.#selectSide(side);
    this.#syncUploadImage();
    this.#updateGroupVisibility();
    this.#render();
    this.#scheduleSnapshot();
  }

  #updateQualityWarning(source, side = 'front') {
    const warning = this.#sideRef(side, 'qualityWarning');
    if (!warning || !this.#config) return;
    warning.hidden = source.isVector || Math.max(source.width, source.height) >= this.#config.artwork.minLongEdge;
  }

  #clearArtwork(side = 'front') {
    const record = this.#sides[side];
    if (!record.state) return;
    record.token += 1;
    record.originalFile = null;
    record.packagedFile = null;
    this.#setArtwork(null, side);
    const input = this.#sideRef(side, 'artworkInput');
    if (input) input.value = '';
    void this.#bindOriginalArtworkToForm(side);
    const name = this.#sideRef(side, 'artworkFileName');
    if (name) name.textContent = name.dataset.empty || 'No file chosen';
    const warning = this.#sideRef(side, 'qualityWarning');
    if (warning) warning.hidden = true;
    record.state.artwork.fileName = '';
    record.state.artwork.position = { ...DEFAULT_ARTWORK_POSITION };
    this.#syncPositionInputs(this.#state.artwork.position);
    this.#updateGroupVisibility();
    this.#clearGeneratedFiles();
    this.#syncUploadImage();
    this.#render();
    this.#scheduleSnapshot();
  }

  async #bindOriginalArtworkToForm(side = 'front') {
    const input = this.#sideRef(side, 'artworkSubmissionInput');
    const record = this.#sides[side];
    const file = record.originalFile;
    const metadata = this.#sideRef(side, 'propArtworkName');
    if (!input) return true;
    if (!file || (side === 'back' && !this.#backEnabled)) {
      attachFileToInput(input, null);
      input.disabled = true;
      if (metadata) { metadata.disabled = true; metadata.value = ''; }
      return true;
    }
    try {
      const packaged = record.packagedFile || await packageArtworkForOrder(file);
      if (!this.isConnected || record.originalFile !== file) return false;
      record.packagedFile = packaged;
      if (side === 'back' && !this.#backEnabled) return this.#bindOriginalArtworkToForm(side);
      input.disabled = false;
      const attached = attachFileToInput(input, packaged);
      if (metadata) {
        metadata.disabled = !attached;
        metadata.value = attached ? `${file.name} (${file.type || 'unknown'}, ${file.size} bytes)` : '';
      }
      return attached;
    } catch (error) {
      console.warn('[live-preview] could not package artwork for order', error);
      attachFileToInput(input, null);
      return false;
    }
  }

  #setArtwork(source, side = this.#activeSide) {
    const record = this.#sides[side];
    if (record.displayArtwork && record.displayArtwork !== record.artwork) releaseArtworkSource(record.displayArtwork);
    if (record.artwork && record.artwork !== source) releaseArtworkSource(record.artwork);
    record.artwork = source;
    record.displayArtwork = source;
    if (side === this.#activeSide) this.#remoteRenderer?.setArtwork?.(this.#displayArtwork);
    if (source && record.state?.artwork.mode !== 'full-color') {
      void this.#prepareDisplayArtwork(source, side);
    }
  }

  async #prepareDisplayArtwork(source, side) {
    if (!this.#config) return;
    const record = this.#sides[side];
    const cleaned = await createCleanedPreview(source, this.#config.artwork, {
      mode: 'smart', color: '#ffffff', tolerance: 28, softness: 2,
    });
    if (!cleaned) return;
    if (!this.isConnected || record.artwork !== source) {
      releaseArtworkSource(cleaned);
      return;
    }
    record.displayArtwork = cleaned;
    if (side === this.#activeSide) this.#remoteRenderer?.setArtwork?.(cleaned);
    this.#scheduleSnapshot();
  }

  async #loadSampleArtwork() {
    const url = this.dataset.sampleArtworkUrl;
    const config = this.#config;
    const controller = this.#controller;
    if (!url || !config || !controller) return;
    const result = await loadArtworkSource({ url, config: config.artwork });
    if ('error' in result) return;
    const source = result.source;
    if (controller.signal.aborted || !this.isConnected) {
      releaseArtworkSource(source);
      return;
    }
    const cleaned = config.colorMode === 'full-color' ? null : await createCleanedPreview(source, config.artwork, {
      mode: 'smart', color: '#ffffff', tolerance: 28, softness: 2,
    });
    if (controller.signal.aborted || !this.isConnected) {
      releaseArtworkSource(cleaned);
      releaseArtworkSource(source);
      return;
    }
    this.#sampleArtwork = source;
    this.#sampleDisplayArtwork = cleaned || source;
    this.#remoteRenderer?.setArtwork?.(this.#displayArtwork);
    this.#render();
  }

  async #setupRemotePreview() {
    const config = this.#config;
    if (!config || !this.isConnected || this.#remoteRenderer) return;

    this.#remoteSetupAttempts += 1;
    const attempt = this.#remoteSetupAttempts;

    try {
      const remote = await createRemotePreviewRenderer({
        domain: this.dataset.previewDomain || '',
        iface: this.#iface,
        config: {
          alignment: config.alignment,
          artwork: config.artwork,
          colorMode: config.colorMode,
          inkColor: config.inkColor,
          attachMockup: config.attachMockup,
          gallerySelector: config.gallerySelector,
        },
      });

      if (!remote || !this.isConnected) {
        this.#clearGeneratedFiles();
        this.#previewReady = true;
        if (attempt < 2) {
          window.setTimeout(() => {
            if (!this.isConnected || this.#remoteRenderer) return;
            void this.#setupRemotePreview();
          }, 1500);
        }
        return;
      }

      const overlay = /** @type {LivePreviewOverlay} */ (document.createElement('live-preview-overlay'));
      overlay.className =
        this.#iface === 3 ? 'apparel-preview-overlay live-preview-overlay' : 'engraving-preview-overlay live-preview-overlay';
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      this.#overlay = overlay;

      const galleryRoot = this.#findMountRoot();
      await remote.mount(overlay, this.#toRemoteState(), {
        galleryRoot,
        externalPlacement: config.positioningEnabled,
        getMediaId: () => this.#hasSecondSide ? this.#state?.preview.mediaId ?? null : this.#uploadImageTarget()?.getAttribute('data-media-id') || null,
        onAttach: (target) => this.#handleOverlayAttached(target),
      });

      if (!this.isConnected) {
        remote.destroy();
        this.#overlay = null;
        return;
      }

      this.#remoteRenderer = remote;
      if (config.positioningEnabled) {
        this.#placementResize = new ResizeObserver(() => {
          cancelAnimationFrame(this.#placementFrame);
          this.#placementFrame = requestAnimationFrame(() => {
            if (this.#remoteRenderer?.mountTarget) this.#handleOverlayAttached(this.#remoteRenderer.mountTarget);
          });
        });
        this.#placementResize.observe(overlay);
        if (overlay.parentElement) this.#placementResize.observe(overlay.parentElement);
      }
      if (this.#hasSecondSide) this.#selectSide(this.#activeSide, undefined, false);
      this.#syncUploadImage();
      if (this.#displayArtwork) remote.setArtwork(this.#displayArtwork);
      this.#render();
      this.#previewReady = true;

      const params = new URLSearchParams(window.location.search);
      if (ALIGNMENT_EDITOR_PARAMS.some((param) => params.has(param))) {
        void this.#loadAlignmentEditor();
      }
    } catch (error) {
      if (
        new URLSearchParams(window.location.search).has('live-preview-debug') ||
        new URLSearchParams(window.location.search).has('patch-preview-debug')
      ) {
        console.info('[live-preview-remote] setup failed', error);
      }
      this.#clearGeneratedFiles();
      this.#previewReady = true;
    }
  }

  /**
   * @param {Element | null} target
   */
  #handleOverlayAttached(target) {
    if (!this.#overlay || !this.#config || !this.#state) return;
    const warning = this.querySelector('[data-position-warning]');
    if (warning) warning.hidden = true;

    if (!target || !this.#canPreviewTarget(target)) {
      this.#overlay.hidden = true;
      this.#syncSampleArtworkLabel();
      return;
    }

    const root = this.#findGalleryRoot();
    const base = getLivePreviewAlignment(this.#config, describeMedia(target, root));
    this.#overlayRotation = base.rotation || 0;

    if (this.#alignmentEditor) {
      this.#overlay.hidden = !this.#displayArtwork;
      this.#overlay.style.pointerEvents = this.#artwork ? 'auto' : 'none';
      this.#state.preview.mediaId = target.getAttribute('data-media-id');
      this.#alignmentEditor.syncToMedia();
      this.#syncSampleArtworkLabel();
      return;
    }

    const alignment = this.#placementAlignment(target);
    if (warning) warning.hidden = !this.#config.positioningEnabled || !alignment.hide || base.hide;
    if (alignment.hide) {
      this.#overlay.hidden = true;
      this.#syncSampleArtworkLabel();
      return;
    }

    applyAlignment(this.#overlay, alignment);
    if (this.#config.positioningEnabled && target !== this.#placementHost) {
      if (this.#placementHost) this.#placementResize?.unobserve(this.#placementHost);
      this.#placementHost = target;
      this.#placementResize?.observe(target);
    }
    this.#overlay.hidden = !this.#displayArtwork;
    this.#overlay.style.pointerEvents = this.#artwork ? 'auto' : 'none';
    this.#state.preview.mediaId = target.getAttribute('data-media-id');
    this.#syncSampleArtworkLabel();
  }

  #placementAlignment(target, state = this.#state) {
    const base = getLivePreviewAlignment(this.#config, describeMedia(target, this.#findGalleryRoot()));
    const rect = target?.getBoundingClientRect();
    const image = target?.querySelector('img');
    const hostAspect = image ? resolveTargetAspectRatio(target, image) : rect?.width / rect?.height || 1;
    const viewBox = this.#overlay?.querySelector('svg')?.viewBox.baseVal;
    const resolved = resolvePreviewPlacement(base, state?.preview.adjustment || { x: 0, y: 0, size: 0 },
      hostAspect, viewBox?.width / viewBox?.height || 1, this.#config.positioningEnabled);
    if (state && !this.#alignmentEditor) {
      state.preview.adjustment = resolved.adjustment;
      if (state === this.#state) this.#syncPreviewAdjustmentInputs(resolved.adjustment);
    }
    return resolved.alignment;
  }

  #syncSampleArtworkLabel() {
    if (this.refs.sampleArtworkLabel) {
      this.refs.sampleArtworkLabel.hidden = !this.#showingSample || !this.#overlay || this.#overlay.hidden;
    }
  }

  #render() {
    if (!this.#state) return;
    const target = this.#remoteRenderer?.mountTarget ?? null;
    this.#remoteRenderer?.update?.(this.#toRemoteState(), {
      target,
      galleryRoot: this.#findGalleryRoot(),
    });
    if (this.#overlay) {
      this.#overlay.hidden = !this.#displayArtwork || !this.#canPreviewTarget(target);
      this.#overlay.style.pointerEvents = this.#artwork ? 'auto' : 'none';
      if (target) this.#handleOverlayAttached(target);
    }
    this.#syncSampleArtworkLabel();
    this.#syncHiddenProperties();
  }

  async #loadAlignmentEditor() {
    if (!this.#overlay || !this.#config || this.#alignmentEditor) return;

    try {
      const mountAlignmentEditor = this.#config.positioningEnabled
        ? (await import('@patch/placement-editor')).mountPreviewPlacementEditor
        : (await import('@patch/alignment-editor')).mountAlignmentEditor;
      const root = this.#findGalleryRoot();
      const isApparel = this.#iface === 3;
      this.#alignmentEditor = mountAlignmentEditor({
        overlay: this.#overlay,
        config: this.#config,
        getMedia: () =>
          this.#remoteRenderer?.mountTarget
            ? describeMedia(this.#remoteRenderer.mountTarget, this.#findGalleryRoot())
            : null,
        listMedia: () => listMedia(this.#findGalleryRoot()),
        viewControls: isApparel && this.#config.positioningEnabled ? {
          backMatch: this.dataset.backImageTag?.trim().toLowerCase() || '',
          isBack: (media) => {
            const target = this.#eligibleMedia(true).find(item => item.getAttribute('data-media-id') === media?.id);
            return target ? this.#isBackMedia(target) : false;
          },
          getMedia: (view) => {
            const target = view === 'back' ? this.#backImageTarget(true)
              : this.#eligibleMedia(true).find(item => !this.#isBackMedia(item) && item.getAttribute('data-media-id') === this.#lastFrontMediaId)
                || this.#eligibleMedia(true).find(item => !this.#isBackMedia(item));
            return target ? describeMedia(target, this.#findGalleryRoot()) : null;
          },
          selectMedia: (mediaId) => {
            const target = this.#eligibleMedia(true).find(item => item.getAttribute('data-media-id') === mediaId);
            if (!target) return;
            if (this.#hasSecondSide) this.#selectSide(this.#sideForMedia(target), target);
            else this.#navigateToMedia(target);
          },
        } : null,
        label: isApparel ? 'Apparel print' : 'Engraving',
        noun: isApparel ? 'print area' : 'engraving',
        onChange: () => { this.#snapshotToken++; this.#render(); this.#scheduleSnapshot(); },
      });
      if (root) this.#alignmentEditor.syncToMedia();
    } catch (error) {
      console.warn('[live-preview] alignment editor unavailable', error);
    }
  }

  /**
   * @returns {ReturnType<typeof buildInitialLivePreviewState>}
   */
  #toRemoteState() {
    const state = this.#state || buildInitialLivePreviewState(this.#config || normalizeLivePreviewConfig({}));
    const displayState = this.#showingSample ? { ...state, artwork: { ...state.artwork, position: { ...DEFAULT_ARTWORK_POSITION } } } : state;
    return this.#hasSecondSide ? displayState : { ...displayState, preview: { ...state.preview, mediaId: this.#uploadImageTarget()?.getAttribute('data-media-id') || null } };
  }

  #updateGroupVisibility() {
    for (const side of ['front', 'back']) {
      const removeButton = this.#sideRef(side, 'artworkRemove');
      if (removeButton) removeButton.hidden = !this.#sides[side].artwork;
    }
    const hasArtwork = Boolean(this.#artwork);
    for (const group of this.refs.groups || []) {
      const key = group.dataset.livePreviewGroup || group.dataset.patchGroup;
      if (key === 'position' || key === 'preview-position') {
        group.hidden = !hasArtwork;
      }
    }
  }

  /**
   * @param {{ x: number, y: number, scale: number, rotation: number }} position
   */
  #syncPositionInputs(position) {
    /** @type {Record<string, number>} */
    const map = {
      'position-x': position.x,
      'position-y': position.y,
      'position-scale': position.scale,
      'position-rotation': position.rotation,
    };
    for (const [control, value] of Object.entries(map)) {
      for (const input of this.querySelectorAll(
        `[data-live-preview-control="${control}"], [data-patch-control="${control}"]`
      )) {
        if (input instanceof HTMLInputElement) input.value = String(value);
      }
    }
  }

  /**
   * @param {{ x: number, y: number, size: number }} adjustment
   */
  #syncPreviewAdjustmentInputs(adjustment) {
    /** @type {Record<string, number>} */
    const map = {
      'preview-x': adjustment.x,
      'preview-y': adjustment.y,
      'preview-size': adjustment.size,
    };
    for (const [control, value] of Object.entries(map)) {
      for (const input of this.querySelectorAll(
        `[data-live-preview-control="${control}"], [data-patch-control="${control}"]`
      )) {
        if (input instanceof HTMLInputElement) input.value = String(value);
      }
    }
  }

  #syncHiddenProperties() {
    const config = this.#config;
    const state = this.#sides.front.state;
    if (!config || !state) return;

    if (this.refs.propMode) {
      this.refs.propMode.disabled = false;
      this.refs.propMode.value =
        state.artwork.mode === 'full-color'
          ? 'Full color'
          : state.artwork.mode === 'single-color'
            ? 'Single color'
            : 'Engraved';
    }

    if (this.refs.propColor) {
      const showColor = state.artwork.mode !== 'full-color';
      this.refs.propColor.disabled = !showColor;
      this.refs.propColor.value = showColor ? state.artwork.inkColor : '';
    }

    if (this.refs.backMockupInput) this.refs.backMockupInput.disabled = !this.#backEnabled;
    for (const name of ['backArtworkSubmissionInput', 'backPropArtworkName', 'backMockupInput']) {
      const input = this.refs[name];
      if (!input) continue;
      input.dataset.frontFirstName ||= input.name;
      input.name = this.#firstOnBack ? input.dataset.frontFirstName.replace(/ Back(?= |\])/g, ' Front') : input.dataset.frontFirstName;
    }
    if (this.refs.propPayload) {
      this.refs.propPayload.disabled = false;
      this.refs.propPayload.value = JSON.stringify({
        mode: state.artwork.mode,
        inkColor: state.artwork.inkColor,
        position: state.artwork.position,
        preview: state.preview.adjustment,
        fileName: state.artwork.fileName || null,
        ...(this.#hasSecondSide ? { firstSide: this.#firstOnBack ? 'back' : 'front', secondSide: this.#firstOnBack ? 'front' : 'back', back: this.#backEnabled ? {
          enabled: true, fileName: this.#sides.back.state.artwork.fileName || null,
          position: this.#sides.back.state.artwork.position,
          preview: this.#sides.back.state.preview.adjustment,
        } : { enabled: false } } : {}),
        previewCleanup: state.artwork.mode === 'engraved' || state.artwork.mode === 'single-color'
          ? { mode: 'smart' } : null,
      });
    }
  }

  #scheduleSnapshot() {
    this.#snapshotToken += 1;
    if (this.#hasSecondSide) this.#clearGeneratedFiles();
    if (this.#snapshotTimer) window.clearTimeout(this.#snapshotTimer);
    const revision = this.#snapshotToken;
    this.#snapshotTimer = window.setTimeout(() => {
      this.#snapshotTimer = null;
      whenIdle(() => {
        if (!this.isConnected || revision !== this.#snapshotToken) return;
        void this.#queueSnapshot();
      }, SNAPSHOT_DEBOUNCE);
    }, SNAPSHOT_DEBOUNCE);
  }

  #queueSnapshot() {
    this.#snapshotWork = (this.#snapshotWork || Promise.resolve())
      .then(() => this.#flushSnapshot())
      .catch(() => this.#clearGeneratedFiles());
    return this.#snapshotWork;
  }

  async #flushSingleSnapshot() {
    const config = this.#config;
    if (!config?.attachMockup || !this.#remoteRenderer?.export || !this.#artwork) {
      this.#clearGeneratedFiles();
      return;
    }

    const token = (this.#snapshotToken += 1);
    const target = this.#remoteRenderer.mountTarget;
    const alignment = target ? this.#placementAlignment(target) : null;
    if (!target || !alignment || alignment.hide) { this.#clearGeneratedFiles(); return; }

    try {
      const result = await this.#remoteRenderer.export({
        placementTarget: target,
        alignment,
        includeVector: config.attachVector === true,
      });
      if (token !== this.#snapshotToken) return;
      // Only mockup / optional vector — never write to the Artwork property.
      if (this.refs.mockupInput) attachFileToInput(this.refs.mockupInput, result?.png || null);
      if (this.refs.vectorInput) attachFileToInput(this.refs.vectorInput, result?.vector || null);
    } catch {
      if (token === this.#snapshotToken) this.#clearGeneratedFiles();
    }
  }

  async #flushSnapshot() {
    if (!this.#hasSecondSide) return this.#flushSingleSnapshot();
    const config = this.#config;
    if (!config?.attachMockup || !this.#remoteRenderer?.export) {
      this.#clearGeneratedFiles();
      return;
    }
    const token = ++this.#snapshotToken;
    if (!this.#snapshotRenderer) {
      const renderer = await createRemotePreviewRenderer({ domain: this.dataset.previewDomain || '', iface: this.#iface,
        config: { ...config } });
      if (!renderer) { this.#clearGeneratedFiles(); return; }
      if (!this.isConnected) { renderer.destroy(); return; }
      await renderer.mount(document.createElement('live-preview-overlay'), this.#toRemoteState());
      this.#snapshotRenderer = renderer;
    }
    for (const side of ['front', 'back']) {
      const input = this.#sideRef(side, 'mockupInput');
      const record = this.#sides[side];
      if (token !== this.#snapshotToken) return;
      if (!record.artwork || (side === 'back' && !this.#backEnabled)) {
        if (input) attachFileToInput(input, null);
        continue;
      }
      const target = this.#hasSecondSide ? this.#targetForSide(side) : this.#remoteRenderer.mountTarget;
      const alignment = target ? this.#placementAlignment(target, record.state) : null;
      if (!target || !alignment || alignment.hide) {
        if (input) attachFileToInput(input, null);
        continue;
      }
      this.#snapshotRenderer.setArtwork(record.displayArtwork);
      this.#snapshotRenderer.update(record.state);
      try {
        const result = await this.#snapshotRenderer.export({ placementTarget: target, alignment, includeVector: config.attachVector === true });
        if (token !== this.#snapshotToken) return;
        if (input) {
          const png = result?.png;
          attachFileToInput(input, png && side === 'back' ? new File([png], `apparel-${this.#physicalSide(side)}-mockup.png`, { type: 'image/png' }) : png || null);
        }
        if (side === 'front' && this.refs.vectorInput) attachFileToInput(this.refs.vectorInput, result?.vector || null);
      } catch {
        if (token === this.#snapshotToken && input) attachFileToInput(input, null);
      }
    }
  }

  #clearGeneratedFiles() {
    for (const input of [this.refs.mockupInput, this.refs.backMockupInput, this.refs.vectorInput]) {
      if (input) attachFileToInput(input, null);
    }
  }

  /** @returns {Element | null} */
  #findGalleryRoot() {
    const selector = this.#config?.gallerySelector || 'media-gallery';
    const section = this.closest('.shopify-section');
    return (
      section?.querySelector(selector) ||
      document.querySelector(selector) ||
      section?.querySelector('media-gallery') ||
      null
    );
  }

  /**
   * @returns {import('./patch-artwork.js').PatchArtworkMessages}
   */
  #artworkMessages() {
    return {
      fileEmpty: this.dataset.errorFileEmpty,
      fileType: this.dataset.errorFileType,
      fileSize: this.dataset.errorFileSize,
      fileRead: this.dataset.errorFileRead,
      fileDimensions: this.dataset.errorFileDimensions,
    };
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (this.refs.errorMessage) this.refs.errorMessage.textContent = message;
    if (this.refs.errors) this.refs.errors.hidden = false;
    this.refs.errorHeading?.focus?.();
  }

  #clearError() {
    if (this.refs.errors) this.refs.errors.hidden = true;
    if (this.refs.errorMessage) this.refs.errorMessage.textContent = '';
  }

  /**
   * Outlines the upload field when checkout is blocked on missing artwork.
   * Focus stays on the error heading, which is what gets announced.
   */
  #highlightArtworkGroup(name = 'artwork') {
    const group = name === 'back-artwork' ? this.refs.backArtworkGroup : (this.refs.groups || []).find(
      (candidate) =>
        (candidate.dataset.livePreviewGroup || candidate.dataset.patchGroup) === name
    );
    if (!group || group.hidden) return;

    group.classList.add('is-highlighted');
    window.setTimeout(() => group.classList.remove('is-highlighted'), ARTWORK_HIGHLIGHT_DURATION);
  }
}

if (!customElements.get('engraving-preview')) {
  customElements.define('engraving-preview', LivePreviewDesigner);
}

if (!customElements.get('apparel-preview')) {
  customElements.define('apparel-preview', class extends LivePreviewDesigner {});
}
